/*   File : manage-competition.js
 *   Author : https://evoqins.com
 *   Description : Manage Campaigns component
 *   Integrations : react-toastify
 *   Version : 1.0.0
*/

import React, { useEffect, useMemo, useState } from 'react';
import { format } from 'date-fns';
import { toast } from 'react-toastify';
import { Modal } from 'bootstrap';
import { useSelector } from 'react-redux';

//Custom component
import { CustomDatePicker, CustomFileUpload, CustomRadio, CustomSelectBox, CustomTextInput, TextArea } from '../FormElements';
import { PrimaryButton, SecondaryButton, TextButton } from '../Buttons';
import { Icon } from '../Icon';
import { Loader, OverLay } from '../Others';
import { APIService, AWSService } from '../../Service';
import { CompetitionCard } from '../Cards';

// style 
import styles from "../../Style/Component/modal.module.scss";

const TYPES = [
    {
        id: 1,
        label: "Yes"
    },
    {
        id: 2,
        label: "No"
    },
];

const TARGET_TYPES = [
    {
        id: 1,
        label: "Users"
    },
    {
        id: 2,
        label: "Areas"
    },
    {
        id: 3,
        label: "Shops"
    },
];


const ManageCompetitionModal = React.memo((props) => {

    const PROFILE_DATA = useSelector((store) => store.Reducer.PROFILE_DATA);

    const [selectedStartDate, setSelectedStartDate] = useState(null);
    const [startDateError, setStartDateError] = useState("");
    const [startDateToggle, setStartDateToggle] = useState(false);

    const [selectedEndDate, setSelectedEndDate] = useState(null);
    const [endDateError, setEndDateError] = useState("");
    const [endDateToggle, setEndDateToggle] = useState(false);

    const [imageUploadLoader, setImageUploadLoader] = useState(true);

    const [createLoader, setCreateLoader] = useState(true);

    const [multiProductTitle, setMultiProductTitle] = useState("");
    const [multiProductTitleError, setMultiProductTitleError] = useState("");

    const [multiProductDescription, setMultiProductDescription] = useState("");
    const [multiProductDescriptionError, setMultiProductDescriptionError] = useState("");

    const [multiProductReward, setMultiProductReward] = useState([""]);
    const [multiProductRewardError, setMultiProductRewardError] = useState([""]);

    const [multiProductStartDate, setMultiProductStartDate] = useState(null);
    const [multiProductStartDateError, setMultiProductStartDateError] = useState("");

    const [multiProductEndDate, setMultiProductEndDate] = useState(null);
    const [multiProductEndDateError, setMultiProductEndDateError] = useState("");

    const [multiProductRules, setMultiProductRules] = useState([""]);
    const [multiProductRulesErrors, setMultiProductRulesErrors] = useState([""]);

    const [multiProductImage, setMultiProductImage] = useState(null);
    const [multiProductImageError, setMultiProductImageError] = useState(null);

    const [multiTargetProducts, setMultiTargetProducts] = useState([null]);
    const [multiTargetProductsError, setMultiTargetProductsError] = useState([]);

    const [multiProductsSalesTarget, setMultiProductsSalesTarget] = useState([""]);
    const [multiProductsSalesTargetError, setMultiProductsSalesTargetError] = useState("");
    const [errorUpdate, setErrorUpdate] = useState(false);

    const [isPreview, setIsPreview] = useState(false);

    const [selectedType, setSelectedType] = useState(1);

    const [selectedUsers, setSelectedUsers] = useState(null);
    const [selectedUsersError, setSelectedUsersError] = useState("");

    const [selectedArea, setSelectedArea] = useState(null);
    const [selectedAreaError, setSelectedAreaError] = useState("");

    const [selectedShop, setSelectedShop] = useState(null);
    const [selectedShopError, setSelectedShopError] = useState("");

    const [filterData, setFilterData] = useState({});
    const [filterLoader, setFilterLoader] = useState(true);

    const [selectedTargetType, setSelectedTargetType] = useState(1);


    useEffect(() => {
        _getFilterData();
    }, []);

    useEffect(() => {
        setCreateLoader(false);
        setImageUploadLoader(false);
    }, []);

    useEffect(() => {
        if (props.edit === true && Object.keys(props.detailData).length !== 0) {
            const multi_target_products = props.detailData.target.map(target => ({
                competition_target_id: target.competition_target_id,
                sales_target: target.sales_target,
                value: target.target_product_id,
                label: target.target_product

            }));

            const sales_target = props.detailData.target.map(target => target.sales_target);
            const updated_point = props.detailData.target.map(target => target.points);


            setMultiTargetProducts(multi_target_products);
            setMultiProductsSalesTarget(sales_target);
            setMultiProductReward(updated_point)

            setMultiProductTitle(props.detailData.title);
            setMultiProductDescription(props.detailData.description);
            setMultiProductStartDate(props.detailData.start_date);
            setMultiProductEndDate(props.detailData.end_date);
            setMultiProductRules(props.detailData.rules);
            setMultiProductImage(props.detailData.image_url);


            setSelectedType(props.detailData.specific_target ? 1 : 2);

            setSelectedTargetType(props.detailData.audience_type_id);

            if (props.detailData.specific_target) {

                let updated_users = props.detailData.target_user?.length > 0 ? props.detailData.target_user.map(item => ({ value: item.id, label: item.name })) : [];

                if (props.detailData.audience_type_id === 1) {
                    setSelectedUsers(updated_users);
                } else if (props.detailData.audience_type_id === 2) {
                    setSelectedArea(updated_users);
                } else if (props.detailData.audience_type_id === 3) {
                    setSelectedShop(updated_users);
                }
            }

        }
    }, [props.detailData]);

    useEffect(() => {
        if (startDateToggle === true) {
            const start_date = new Modal(document.getElementById('start-date-modal'));
            start_date.show();
        }
    }, [startDateToggle]);

    useEffect(() => {
        if (endDateToggle === true) {
            const end_date = new Modal(document.getElementById('end-date-modal'));
            end_date.show();
        }
    }, [endDateToggle]);

    useEffect(() => {
        setStartDateError("");
    }, [selectedStartDate]);

    useEffect(() => {
        setEndDateError("");
    }, [selectedEndDate]);

    useEffect(() => {
        setSelectedUsersError("");
    }, [selectedUsers, selectedType, selectedTargetType]);


    useEffect(() => {
        setSelectedAreaError("");
    }, [selectedArea, selectedType, selectedTargetType]);


    useEffect(() => {
        setSelectedShopError("");
    }, [selectedShop, selectedType, selectedTargetType]);


    // handle Title Change
    const _handleChangeTitle = (value) => {
        if (value.length <= 60) {
            setMultiProductTitle(value);
        }
        setMultiProductTitleError("");
    };

    // handle Description Change
    const _handleDescriptionChange = (value) => {
        if (value.length <= 120) {
            setMultiProductDescription(value);
        }
        setMultiProductDescriptionError("");
    };


    // handle multi product select 
    const _handleMultiProductSelect = (selected, i) => {
        let product_list = [...multiTargetProducts];
        product_list[i] = selected;
        setMultiTargetProducts([...product_list]);

        // Updating errors
        let errors = [...multiTargetProductsError];
        errors[i] = "";
        setMultiTargetProductsError([...errors]);
    };

    // handle add more product
    const _handleAddMoreProduct = () => {
        setMultiTargetProducts([...multiTargetProducts, null]);
        setMultiProductsSalesTarget([...multiProductsSalesTarget, ""]);
        setMultiTargetProductsError([...multiTargetProductsError, ""]);
        setMultiProductsSalesTargetError([...multiProductsSalesTargetError, ""]);

        // _handleUpdateTotalSalesTarget();
    };

    // handle Change Multi Product Sales Target
    const _handleChangeMultiProductSalesTarget = (value, i) => {
        let sales_target = [...multiProductsSalesTarget];
        sales_target[i] = value;
        setMultiProductsSalesTarget([...sales_target]);

        // _handleUpdateTotalSalesTarget();

        // Updating errors
        let errors = [...multiProductsSalesTargetError];
        errors[i] = "";
        setMultiProductsSalesTargetError([...errors]);
    };

    const _handleChangeMultiProductReward = (value, index) => {
        const updated = [...multiProductReward];
        updated[index] = value;
        setMultiProductReward(updated);

        const errors = [...multiProductRewardError];
        errors[index] = "";
        setMultiProductRewardError(errors);
    };


    // handle remove product 
    const _handleRemoveProduct = (key) => {
        let product_list = [...multiTargetProducts];
        product_list = product_list.filter((_, index) => index !== key);
        setMultiTargetProducts(product_list);

        let sales_target_list = [...multiProductsSalesTarget];
        sales_target_list = sales_target_list.filter((_, index) => index !== key);
        setMultiProductsSalesTarget(sales_target_list);

        const updated_target_products_error = [...multiTargetProductsError];
        updated_target_products_error.splice(key, 1);
        setMultiTargetProductsError(updated_target_products_error);

        const updated_target_products_sales_error = [...multiProductsSalesTargetError];
        updated_target_products_sales_error.splice(key, 1);
        setMultiProductsSalesTargetError(updated_target_products_sales_error);

        // _handleUpdateTotalSalesTarget();

    };

    // const _handleTotalSalesTargetChange = (value) => {
    //     setTotalSalesTarget(value);
    //     setTotalSalesTargetError("");
    // };

    // const _handleUpdateTotalSalesTarget = () => {
    //     let sales_target = [...multiProductsSalesTarget];
    //     let total_sales_target = sales_target.reduce((a, b) => a + b);
    //     setTotalSalesTarget(total_sales_target);
    // };



    // handle adding a new multiproduct rule
    const _handleAddMoreMultiProductRule = () => {
        setMultiProductRules([...multiProductRules, ""]);
        setMultiProductRulesErrors([...multiProductRulesErrors, ""]);
    };


    //handle removing multi product rule
    const _handleRemoveMultiProductRule = (index) => {
        const updated_rule = [...multiProductRules];
        updated_rule.splice(index, 1);
        setMultiProductRules(updated_rule);

        const updated_error = [...multiProductRulesErrors];
        updated_error.splice(index, 1);
        setMultiProductRulesErrors(updated_error);
    };

    // handle change
    const _handleChangeMultiProductRule = useMemo(() => (index, value) => {
        const updated_rule = [...multiProductRules];
        updated_rule[index] = value;
        setMultiProductRules(updated_rule);

        setMultiProductRulesErrors(prev => {
            const updated_errors = [...prev];
            updated_errors[index] = "";
            return updated_errors;
        });

    }, [multiProductRules]);

    // handle close modal
    const _handleCloseModal = () => {
        var button = document.getElementById("manage-competition-modal-close");
        button.click();
        setSelectedStartDate(null);
        setSelectedEndDate(null);
        setStartDateError(null);
        setEndDateError(null);

        setIsPreview(false);

        props.handleClose();
    };

    // start date toggle show an hide
    const _handleStartDateToggle = () => {
        setStartDateToggle(!startDateToggle);
    };

    // handle select start date
    const _handleSelectedStartDate = (day) => {

        setMultiProductStartDate(day);
        setMultiProductStartDateError("");

        setStartDateToggle(false);
        _handleCloseStartDateModal();
        setSelectedEndDate(null);
    };

    // handle close start date modal 
    const _handleCloseStartDateModal = () => {
        var button = document.getElementById("start-date-modal-close");
        button.click();
        setStartDateToggle(false);
    };

    // end date toggle show an hide
    const _handleEndDateToggle = () => {
        setEndDateToggle(!endDateToggle);
    };

    // handle select end date
    const _handleSelectedEndDate = (day) => {
        setMultiProductEndDate(day);
        setMultiProductEndDateError("");

        setEndDateToggle(false);
        _handleCloseEndDateModal();
    };

    // handle close end date modal 
    const _handleCloseEndDateModal = () => {
        var button = document.getElementById("end-date-modal-close");
        button.click();
        setEndDateToggle(false);
    };

    // handle upload image
    const _uploadedImage = (url) => {
        setImageUploadLoader(true);
        _getPresignedUrl(url);
    };

    // handle Image change 
    const _handleChangeImage = () => {
        setMultiProductImage(null)
    };

    // handle focus error
    const _focusError = (error_element_id) => {
        if (document.getElementById(error_element_id)) {
            document.getElementById(error_element_id).scrollIntoView({ behavior: 'smooth', block: 'start' });
        }

    };


    // handle add admin submit
    const _handleCreateCompetition = () => {

        let is_valid = true;
        let error_field = [];


        if (multiProductTitle.trim().length === 0) {
            setMultiProductTitleError("Title is required")
            is_valid = false;
            error_field.push('multi-product-title');
        }

        if (multiProductDescription.trim().length === 0) {
            setMultiProductDescriptionError("Description is required")
            is_valid = false;
            error_field.push('multi-product-description');
        }

        if (selectedType === 1) {
            if (selectedTargetType === 1 && selectedUsers === null) {
                setSelectedUsersError("Please select users");
                is_valid = false;
                error_field.push('selectedUsers');
            }

            if (selectedTargetType === 2 && selectedArea === null) {
                setSelectedAreaError("Please select area");
                is_valid = false;
                error_field.push('selectedArea');
            }

            if (selectedTargetType === 3 && selectedShop === null) {
                setSelectedShopError("Please select shop");
                is_valid = false;
                error_field.push('selectedShop');
            }
        }

        if (multiProductReward.length === 0) {
            setMultiProductRewardError("Reward is required");
            is_valid = false;
            error_field.push('multi-product-reward');
        }

        if (multiProductStartDate === null) {
            setMultiProductStartDateError("Start date is required")
            is_valid = false;
            error_field.push('multi-product-startDate');
        }

        if (multiProductEndDate === null) {
            setMultiProductEndDateError("End date is required")
            is_valid = false;
            error_field.push('multi-product-endDate');
        }

        if (multiProductImage === null) {
            setMultiProductImageError("Image is required")
            is_valid = false;
            error_field.push('multi-product-image');
        }

        const new_rules_error = multiProductRules.map((rule) => (rule.trim().length === 0 ? "Rule is required" : ""));
        setMultiProductRulesErrors(new_rules_error);

        // Check if there are any errors
        if (new_rules_error.some((error) => error !== "")) {
            for (let i = 0; i < new_rules_error.length; i++) {
                const error = new_rules_error[i];
                if (error.trim().length !== 0) {
                    error_field.push(`multi-product-rule-${i}`);
                }
            }

            is_valid = false;
        }

        const new_product_error = multiTargetProducts.map((product) => (product === null ? "Select target product" : ""));
        setMultiTargetProductsError(new_product_error);

        // Check if there are any errors
        if (new_product_error.some((error) => error !== "")) {
            for (let i = 0; i < new_product_error.length; i++) {
                const error = new_product_error[i];
                if (error.trim().length !== 0) {
                    error_field.push(`multi-product-${i}`);
                }
            }

            is_valid = false;
        }



        const new_sales_target_errors = multiProductsSalesTarget.map((target) => (target.length === 0 ? "Sales target is required" : ""));
        setMultiProductsSalesTargetError(new_sales_target_errors);

        const updated_reward_errors = multiProductReward.map((reward) => (reward.length === 0 ? "Point is required" : ""));
        setMultiProductRewardError(updated_reward_errors);

        // Check if there are any errors
        if (new_sales_target_errors.some((error) => error !== "")) {
            for (let i = 0; i < new_sales_target_errors.length; i++) {
                const error = new_sales_target_errors[i];
                if (error.trim().length !== 0) {
                    error_field.push(`multi-product-sales-target-${i}`);
                }
            }

            is_valid = false;
        }

        setErrorUpdate(prev => !prev);

        if (is_valid === true) {
            setIsPreview(true);
        } else {
            if (error_field.length > 0) {
                _focusError(error_field[0]);
            }
        }
    };

    const _handleConfirmCreate = () => {
        setCreateLoader(true);
        if (props.edit === true) {
            _updateCompetition();
        } else {
            _createCompetition();
        }
    }

    // get presigned url
    async function _getPresignedUrl(file) {
        let is_post = true;
        let url = 'general/file-upload';
        let data = {
            "file": file.name,
            "is_public": true
        };
        try {
            let response = await APIService(is_post, url, data);
            if (response.status_code === 200) {
                let aws_fields = response.data.upload_info.fields;
                let aws_url = response.data.upload_info.url;

                try {
                    let aws_response = await AWSService(aws_fields, file, aws_url);
                    if (aws_response.status === 'ok') {
                        setMultiProductImage(response.data.view_info);
                        setMultiProductImageError("");
                    } else {
                        toast.dismiss();
                        toast.error(aws_response.message, {
                            type: "error",
                            className: "e-toast",
                        });
                    }
                } catch (error) { }
            } else {
                toast.dismiss();
                toast.error(response.message, {
                    type: "error",
                    className: "e-toast",
                });
            }
        } catch (err) { }
        setImageUploadLoader(false);
    };

    // API - Create competition
    const _createCompetition = () => {

        const product_data = multiTargetProducts.map((product, index) => {
            if (product !== null && multiProductsSalesTarget[index] !== "") {
                return {
                    product_id: product.value,
                    sales_target: multiProductsSalesTarget[index],
                    points: multiProductReward[index]
                };
            }
            return null;
        }).filter(item => item !== null);

        const updated_selected_users = selectedUsers !== null ? selectedUsers.map(item => item.value) : [];
        const updated_selected_area = selectedArea !== null ? selectedArea.map(item => item.value) : [];
        const updated_selected_shop = selectedShop !== null ? selectedShop.map(item => item.value) : [];

        let is_post = true;
        let url = 'CA/competition/create';
        let data = {
            "title": multiProductTitle,
            "description": multiProductDescription,
            "sales_target": Number(multiProductsSalesTarget),
            "start_date": format(multiProductStartDate, 'yyyy-MM-dd'),
            "end_date": format(multiProductEndDate, 'yyyy-MM-dd'),
            "rules": multiProductRules,
            "image_url": multiProductImage,
            "target": product_data,

            // "is_specific_user": selectedType === 1 ? true : false,
            // "country_admin_id": selectedType === 1 ? selectedUsers?.value : null,
            // "area_id": selectedType === 1 ? selectedArea?.value : null,
            // "store_id": selectedType === 1 ? selectedShop?.value : null,


            "specific_target": selectedType === 1 ? true : false,
            "audience_type_id":selectedType === 1 ? selectedTargetType : null,
            "user_ids": selectedType === 1 && selectedTargetType === 1 ? updated_selected_users : [],
            "city_ids": selectedType === 1 && selectedTargetType === 2 ? updated_selected_area : [],
            "store_ids": selectedType === 1 && selectedTargetType === 3 ? updated_selected_shop : []
        };

        APIService(is_post, url, data).then((response) => {
            if (response.status_code === 200) {

                toast.dismiss();
                toast.success("Competition created successfully", {
                    type: "success",
                    className: "e-toast",
                });

                _handleCloseModal();
                props.success();
                setIsPreview(false);

            } else {
                toast.dismiss();
                toast.error(response.message, {
                    type: "error",
                    className: "e-toast",
                });
            }
            setCreateLoader(false);
        });
    };

    // API - Update competition
    const _updateCompetition = () => {

        const product_data = multiTargetProducts.map((product, index) => {
            if (product !== null && multiProductsSalesTarget[index] !== "") {
                return {
                    product_id: product.value,
                    sales_target: multiProductsSalesTarget[index],
                    points: multiProductReward[index],
                    competition_target_id: product.competition_target_id
                };
            }
            return null;
        }).filter(item => item !== null);

        const updated_selected_users = selectedUsers !== null ? selectedUsers.map(item => item.value) : [];
        const updated_selected_area = selectedArea !== null ? selectedArea.map(item => item.value) : [];
        const updated_selected_shop = selectedShop !== null ? selectedShop.map(item => item.value) : [];

        let is_post = true;
        let url = 'CA/competition/update';
        let data = {
            "competition_id": props.detailData.competition_id,
            "title": multiProductTitle,
            "description": multiProductDescription,
            "sales_target": Number(multiProductsSalesTarget),
            "start_date": format(multiProductStartDate, 'yyyy-MM-dd'),
            "end_date": format(multiProductEndDate, 'yyyy-MM-dd'),
            "rules": multiProductRules,
            "image_url": multiProductImage,
            "target": product_data,

            // "is_specific_user": selectedType === 1 ? true : false,
            // "country_admin_id": selectedType === 1 ? selectedUsers?.value : null,
            // "area_id": selectedType === 1 ? selectedArea?.value : null,
            // "store_id": selectedType === 1 ? selectedShop?.value : null,

            "specific_target": selectedType === 1 ? true : false,
            "audience_type_id":selectedType === 1 ? selectedTargetType : null,
            "user_ids": selectedType === 1 && selectedTargetType === 1 ? updated_selected_users : [],
            "city_ids": selectedType === 1 && selectedTargetType === 2 ? updated_selected_area : [],
            "store_ids": selectedType === 1 && selectedTargetType === 3 ? updated_selected_shop : []
        };

        APIService(is_post, url, data).then((response) => {
            if (response.status_code === 200) {
                _handleCloseModal();
                props.success();
                setIsPreview(false);
            } else {
                toast.dismiss();
                toast.error(response.message, {
                    type: "error",
                    className: "e-toast",
                })
            }
            setCreateLoader(false);
        });
    };

    // API - Get filter data
    const _getFilterData = () => {
        let is_post = false;
        let url = 'CA/competition/filter-data';
        let data = {};

        APIService(is_post, url, data).then((response) => {
            if (response.status_code === 200) {
                setFilterData(response.data);
            } else {
                setFilterData({});
            }
            setFilterLoader(false);
        });
    };

    return (
        <div className="modal fade"
            id="manage-competition-modal"
            data-bs-backdrop="static"
            data-bs-keyboard="false"
            tabIndex="-1"
            aria-labelledby="manageCompetitionModalLabel"
            aria-hidden="true">
            <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable e-border-radius-24">
                <div className="modal-content e-border-radius-12 overflow-hidden">
                    <img src={require("../../Assets/Image/CountryAdmin/close-icon.svg").default}
                        draggable={false}
                        id='manage-competition-modal-close'
                        className='d-none '
                        alt='close'
                        data-bs-dismiss="modal" />
                    <div className="modal-header align-items-start flex-column px-4 pt-4 pb-2 border-0">
                        <div className='d-flex justify-content-between w-100 mb-2'>
                            {isPreview === false ?
                                <h4 className="mb-0 e-primary-font-playfair e-text-dark-jungle-green e-font-18 e-font-weight-500 line-height-28px">
                                    {props.edit === true ? "Edit Campaign Setup" : "Campaign Setup"}
                                </h4>
                                :
                                <h4 className="mb-0 e-primary-font-playfair e-text-dark-jungle-green e-font-18 e-font-weight-500 line-height-28px">
                                    Preview Campaign
                                </h4>
                            }
                            <Icon icon="close-icon"
                                className='cursor-pointer'
                                size="24px"
                                onPress={_handleCloseModal} />
                        </div>
                        <p className='mb-0 e-text-davys-grey e-alt-font-noto-sans e-font-14 e-font-weight-400 line-height-20px'>
                            You can add one or more products with individual targets and points.
                        </p>
                    </div>

                    {(props.loading || filterLoader) ?
                        <Loader className="h-40vh" />
                        :
                        <div className={`${styles.e_modal_body_wrapper} pt-2 modal-body ps-4 pe-3 me-2 pb-0 h-422px overflow-auto e-modal-body-wrapper`}>
                            {isPreview === false ?
                                <div className={`pb-2`}>
                                    <div id="multi-product-title" className='position-relative'>
                                        <CustomTextInput label="Campaign title"
                                            placeholder="Enter campaign title"
                                            type="text"
                                            require
                                            value={multiProductTitle}
                                            error={multiProductTitleError}
                                            className="w-100 padding-10px-tb"
                                            labelStyle="e-font-weight-500 e-text-raisin-black"
                                            handleChange={_handleChangeTitle} />

                                        <p className='mb-0 position-absolute end-0 bottom-0 e-text-flint e-font-12 e-alt-font-noto-sans e-font-weight-400 line-height-16px'>
                                            {multiProductTitle.length}/60
                                        </p>
                                    </div>

                                    <div id='multi-product-description' className='position-relative'>
                                        <TextArea label="Campaign description"
                                            placeholder="Enter your text..."
                                            type="text"
                                            rows="3"
                                            require
                                            value={multiProductDescription}
                                            error={multiProductDescriptionError}
                                            className="px-3 padding-10px-tb w-100"
                                            handleChange={_handleDescriptionChange} />

                                        <p className='mb-0 position-absolute end-0 bottom--16px e-text-flint e-font-12 e-alt-font-noto-sans e-font-weight-400 line-height-16px'>
                                            {multiProductDescription.length}/120
                                        </p>
                                    </div>

                                    <div className='pb-3 mb-1 pt-2'>
                                        <CustomRadio label="Do you want to select specific users for this campaign?"
                                            required={true}
                                            data={TYPES}
                                            gap=" gap-3"
                                            selected={selectedType}
                                            onClick={(id) => {
                                                setSelectedType(id);

                                                setSelectedTargetType(1);
                                                setSelectedUsers(null);
                                                setSelectedArea(null);
                                                setSelectedShop(null);
                                            }} />


                                        {selectedType === 1 &&
                                            <div className={` pt-3 mt-1 `}>

                                                <div className='pb-3'>
                                                    <CustomRadio label="Audience type"
                                                        required={true}
                                                        data={TARGET_TYPES}
                                                        gap=" gap-3"
                                                        selected={selectedTargetType}
                                                        onClick={(id) => {
                                                            setSelectedTargetType(id);
                                                            setSelectedUsers(null);
                                                            setSelectedArea(null);
                                                            setSelectedShop(null);
                                                        }} />
                                                </div>

                                                {selectedTargetType === 1 ?
                                                    <div id={`selectedUsers`} >
                                                        <CustomSelectBox label="Select users"
                                                            value={selectedUsers}
                                                            require
                                                            wrapperHeight="h-56px"
                                                            options={filterData?.users || []}
                                                            error={selectedUsersError}
                                                            isMulti={true}
                                                            className="e-modal-select"
                                                            isSearchable={true}
                                                            menuPortalTarget={document.body}
                                                            menuPosition="fixed"
                                                            selectChange={(value) => setSelectedUsers(value)}
                                                        />
                                                    </div>

                                                    :
                                                    selectedTargetType === 2 ?
                                                        <div id={`selectedArea`}>
                                                            <CustomSelectBox label="Select areas"
                                                                value={selectedArea}
                                                                require
                                                                wrapperHeight="h-56px"
                                                                options={filterData?.areas || []}
                                                                error={selectedAreaError}
                                                                isMulti={true}
                                                                className="e-modal-select"
                                                                isSearchable={true}
                                                                menuPortalTarget={document.body}
                                                                menuPosition="fixed"
                                                                selectChange={(value) => setSelectedArea(value)}
                                                            />
                                                        </div>
                                                        :
                                                        selectedTargetType === 3 ?
                                                            <div id={`selectedShop`}>
                                                                <CustomSelectBox label="Select shops"
                                                                    value={selectedShop}
                                                                    require
                                                                    wrapperHeight="h-56px"
                                                                    options={filterData?.stores || []}
                                                                    error={selectedShopError}
                                                                    isMulti={true}
                                                                    className="e-modal-select"
                                                                    isSearchable={true}
                                                                    menuPortalTarget={document.body}
                                                                    menuPosition="fixed"
                                                                    selectChange={(value) => setSelectedShop(value)}
                                                                />
                                                            </div>
                                                            :
                                                            null
                                                }
                                            </div>
                                        }
                                    </div>

                                    <>
                                        <div className='d-flex flex-column gap-2'>
                                            {multiTargetProducts.map((target_product, index) => (
                                                <div className='e-border-1px-indigo padding-12px-top padding-12px-lr e-border-radius-8'>

                                                    <div className='d-flex align-items-center justify-content-between pb-3'>
                                                        <p className='mb-0 e-text-indigo e-font-14 e-alt-font-noto-sans e-font-weight-400 line-height-18px'>
                                                            Product {index + 1}
                                                        </p>

                                                        {multiTargetProducts.length > 1 &&
                                                            <TextButton name="Remove"
                                                                className="e-text-lust e-font-weight-400"
                                                                onPress={() => _handleRemoveProduct(index)} />
                                                        }
                                                    </div>

                                                    <div className='row align-items-center gx-3' key={index}>
                                                        <div className={`col-6`}
                                                            id={`multi-product-${index}`}>
                                                            <CustomSelectBox label="Target product"
                                                                value={target_product}
                                                                require
                                                                wrapperHeight="h-56px"
                                                                options={props.product}
                                                                error={multiTargetProductsError[index]}
                                                                className="e-modal-select"
                                                                isSearchable={true}
                                                                menuPortalTarget={document.body}
                                                                menuPosition="fixed"
                                                                selectChange={(selected) => _handleMultiProductSelect(selected, index)}
                                                            />
                                                        </div>

                                                        <div className='col-5'
                                                            id={`multi-product-sales-target-${index}`}>
                                                            <CustomTextInput label={PROFILE_DATA.country === "Egypt" ? "Sales target (units)" : "Sales target (ltrs)"}
                                                                placeholder="50"
                                                                type="number"
                                                                require
                                                                height="h-56px"
                                                                value={multiProductsSalesTarget[index]}
                                                                error={multiProductsSalesTargetError[index]}
                                                                className="w-100 padding-10px-tb"
                                                                labelStyle="e-text-raisin-black"
                                                                handleChange={(value) => _handleChangeMultiProductSalesTarget(value, index)} />
                                                        </div>

                                                        <div id='multi-product-reward' className='col-6'>
                                                            <CustomTextInput label="Points"
                                                                placeholder="10"
                                                                type="number"
                                                                require
                                                                value={multiProductReward[index]}
                                                                error={multiProductRewardError[index]}
                                                                className="w-100 padding-10px-tb"
                                                                labelStyle="e-font-weight-500 e-text-raisin-black"
                                                                handleChange={(value) =>
                                                                    _handleChangeMultiProductReward(value, index)
                                                                } />
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>

                                        {multiTargetProducts.length < 5 &&
                                            <p className='mt-1 d-inline-block mb-3 e-text-indigo e-font-14 e-alt-font-noto-sans e-font-weight-600 line-height-18px cursor-pointer'
                                                onClick={_handleAddMoreProduct} >
                                                + <span className='cursor-pointer text-decoration-hover-underline'>Add another product</span>
                                            </p>
                                        }
                                    </>

                                    {/* date select */}
                                    <div className='row'>
                                        <div className='col-6 pe-2'
                                            id="multi-product-startDate">
                                            <p className='mb-0 e-text-raisin-black e-alt-font-noto-sans e-font-14 e-font-weight-400 line-height-20px'>
                                                Start date<span className='e-text-lust'>*</span>
                                            </p>

                                            <div className='pt-2 h-70px position-relative'>
                                                <div className={`${startDateToggle ? 'e-border-1px-indigo' : 'e-border-1px-platinum-light'} e-display-flex gap-0-4px e-border-radius-6 padding-14px-tb px-2 cursor-pointer`}
                                                    onClick={_handleStartDateToggle}>
                                                    <img src={require("../../Assets/Image/CountryAdmin/Notification/calendar-icon.svg").default}
                                                        draggable={false}
                                                        alt='calender-icon' />
                                                    {
                                                        multiProductStartDate !== null ?
                                                            <>
                                                                <p className='mb-0 e-text-raisin-black e-alt-font-noto-sans e-font-12 e-font-weight-400 line-height-16px'>
                                                                    {format(multiProductStartDate, 'PP')}
                                                                </p>

                                                            </>
                                                            :
                                                            <p className='mb-0 e-text-davys-grey e-alt-font-noto-sans e-font-12 e-font-weight-400 line-height-16px'>
                                                                Choose start date
                                                            </p>
                                                    }

                                                </div>
                                                <p className='mb-0 e-error-message e-alt-font-noto-sans e-font-12 line-height-12px padding-2-top'>
                                                    {multiProductStartDateError}
                                                </p>

                                                {
                                                    multiProductStartDate !== null &&
                                                    <Icon icon="close-icon"
                                                        className='position-absolute top-20px right-8px cursor-pointer'
                                                        size="20px"
                                                        onPress={() => {
                                                            setMultiProductEndDate(null);
                                                            setMultiProductStartDate(null);
                                                        }} />
                                                }
                                            </div>

                                            {/* date picker */}
                                            {startDateToggle &&
                                                <div className="modal fade" id="start-date-modal" data-bs-backdrop="false" tabIndex="-1" aria-labelledby="starDateModalLabel" aria-hidden="true">
                                                    <div className="modal-dialog modal-dialog-centered e-border-radius-24">
                                                        <button id='start-date-modal-close'
                                                            className='d-none '
                                                            data-bs-dismiss="modal" />
                                                        <div className="modal-content w-358px  mx-auto border-0 bg-transparent">
                                                            <OverLay className="e-bg-overlay " isOpen={startDateToggle} onClose={_handleCloseStartDateModal} />
                                                            <CustomDatePicker type="modal"
                                                                selected={multiProductStartDate}
                                                                onSelect={_handleSelectedStartDate} />
                                                        </div>
                                                    </div>
                                                </div>
                                            }
                                        </div>

                                        {/* End date picker */}
                                        <div className='col-6 ps-2'
                                            id={`multi-product-endDate`}>
                                            <p className='mb-0 e-text-raisin-black e-alt-font-noto-sans e-font-14 e-font-weight-400 line-height-20px'>
                                                End date<span className='e-text-lust'>*</span>
                                            </p>
                                            <div className='pt-2 h-70px position-relative'>
                                                <div className={`${endDateToggle ? 'e-border-1px-indigo' : 'e-border-1px-platinum-light'} ${multiProductStartDate === null ? "e-disable" : ''} e-display-flex gap-0-4px e-border-radius-6 padding-14px-tb px-2 cursor-pointer`}
                                                    onClick={_handleEndDateToggle}>
                                                    <img src={require("../../Assets/Image/CountryAdmin/Notification/calendar-icon.svg").default}
                                                        draggable={false}
                                                        alt='calender-icon' />
                                                    {
                                                        multiProductEndDate !== null ?
                                                            <p className='mb-0 e-text-raisin-black e-alt-font-noto-sans e-font-12 e-font-weight-400 line-height-16px'>
                                                                {format(multiProductEndDate, 'PP')}
                                                            </p>
                                                            :
                                                            <p className='mb-0 e-text-davys-grey e-alt-font-noto-sans e-font-12 e-font-weight-400 line-height-16px'>
                                                                Choose end date
                                                            </p>
                                                    }
                                                </div>
                                                <p className='mb-0 e-error-message e-alt-font-noto-sans e-font-12 line-height-12px padding-2-top'>
                                                    {multiProductEndDateError}
                                                </p>

                                                {
                                                    multiProductEndDate !== null &&
                                                    <Icon icon="close-icon"
                                                        className='position-absolute top-20px right-8px cursor-pointer'
                                                        size="20px"
                                                        onPress={() => setMultiProductEndDate(null)} />
                                                }
                                            </div>

                                            {/*End date picker */}
                                            {
                                                endDateToggle &&
                                                <div className="modal fade" id="end-date-modal" data-bs-backdrop="false" tabIndex="-1" aria-labelledby="endDateModalLabel" aria-hidden="true" >
                                                    <div className="modal-dialog modal-dialog-centered e-border-radius-24">
                                                        <button id='end-date-modal-close'
                                                            className='d-none '
                                                            data-bs-dismiss="modal" />
                                                        <div className="modal-content w-358px  mx-auto border-0 bg-transparent">
                                                            <OverLay className="e-bg-overlay " isOpen={endDateToggle} onClose={_handleCloseEndDateModal} />
                                                            <CustomDatePicker type="modal"
                                                                selected={multiProductEndDate}
                                                                minDate={multiProductStartDate}
                                                                onSelect={_handleSelectedEndDate} />
                                                        </div>
                                                    </div>
                                                </div>
                                            }
                                        </div>
                                    </div>

                                    {/* Add rules */}
                                    <>
                                        {multiProductRules.map((rule, index) => (
                                            <div id={`multi-product-rule-${index}`}
                                                key={index}
                                                className='row'>
                                                <div className="col-11">
                                                    <CustomTextInput
                                                        label={index === 0 ? "Competition terms and conditions" : ""}
                                                        placeholder={`Rule ${index + 1}`}
                                                        type="text"
                                                        height="h-56px"
                                                        require
                                                        value={rule}
                                                        error={multiProductRulesErrors[index]}
                                                        className="w-100 padding-10px-tb"
                                                        handleChange={(e) => _handleChangeMultiProductRule(index, e)}
                                                    />

                                                </div>

                                                <div className={`${multiProductRules.length > 1 ? "" : "pe-none opacity-50"} col-1 text-end ${index === 0 ? 'pt-4 mt-2' : 'pt-1'}`}>
                                                    <img src={require("../../Assets/Image/CountryAdmin/delete.svg").default}
                                                        draggable={false}
                                                        className='cursor-pointer'
                                                        alt='delete'
                                                        onClick={() => {
                                                            if (multiProductRules.length > 1) {
                                                                _handleRemoveMultiProductRule(index)
                                                            }
                                                        }} />
                                                </div>
                                            </div>
                                        ))}

                                        {multiProductRules.length < 5 &&
                                            <p className='d-inline-block mb-0 e-text-indigo e-font-14 e-alt-font-noto-sans e-font-weight-600 line-height-18px cursor-pointer'
                                                onClick={_handleAddMoreMultiProductRule} >
                                                + <span className='cursor-pointer text-decoration-hover-underline'>Set new rule</span>
                                            </p>
                                        }
                                    </>

                                    {/* file upload */}
                                    <div className='pt-1'
                                        id="multi-product-image">
                                        <CustomFileUpload label="Upload image"
                                            placeholder="Drop your image here"
                                            require
                                            resolution="1548px * 477px"
                                            requiredWidth={1548}
                                            requiredHeight={477}
                                            file={multiProductImage}
                                            fileType={["JPG", "PNG", "JPEG"]}
                                            error={multiProductImageError}
                                            loader={imageUploadLoader}
                                            errorUpdate={errorUpdate}
                                            handleChange={_uploadedImage} />

                                        {multiProductImage !== null &&
                                            <div className='mb-2 margin-neg-20px-top'>
                                                <TextButton name="Change image"
                                                    onPress={_handleChangeImage} />
                                            </div>
                                        }
                                    </div>
                                </div>
                                :
                                <>
                                    <CompetitionCard data={{
                                        image_url: multiProductImage,
                                        title: multiProductTitle,
                                        description: multiProductDescription,

                                        specific_target: selectedType === 1 ? true : false,
                                        audience_type_id: selectedTargetType,
                                        target_user: selectedType === 1 && selectedTargetType === 1 ? selectedUsers : selectedTargetType === 2 ? selectedArea : selectedTargetType === 3 ? selectedShop : null,
                                        // users_data: {
                                        //     users: selectedUsers || null,
                                        //     areas: selectedArea || null,
                                        //     stores: selectedShop || null,
                                        // },

                                        start_date: format(multiProductStartDate, 'yyyy-MM-dd'),
                                        end_date: format(multiProductEndDate, 'yyyy-MM-dd'),
                                        target: multiTargetProducts.map((product, index) => {
                                            if (product !== null && multiProductsSalesTarget[index] !== "") {
                                                return {
                                                    target_product: product.label,
                                                    sales_target: multiProductsSalesTarget[index],
                                                    points: multiProductReward[index]
                                                };
                                            }
                                            return null;
                                        }).filter(item => item !== null),
                                        rules: multiProductRules
                                    }}
                                        preview={true}
                                        handleNavigate={() => { }}
                                    />
                                </>
                            }
                        </div>
                    }

                    {isPreview === false ?
                        <div className='row pt-3 px-4 pb-4'>
                            <div className='col-6'>
                                <SecondaryButton name="Cancel"
                                    className="w-100"
                                    onPress={_handleCloseModal} />
                            </div>
                            <div className='col-6'>
                                <PrimaryButton name={"Preview"}
                                    className="w-100"
                                    onPress={_handleCreateCompetition} />
                            </div>
                        </div>
                        :
                        <div className='row pt-3 px-4 pb-4'>
                            <div className='col-6'>
                                <SecondaryButton name="Back"
                                    className="w-100"
                                    onPress={() => setIsPreview(false)} />
                            </div>
                            <div className='col-6'>
                                <PrimaryButton name={props.edit === true ? "Update competition" : "Launch competition"}
                                    className="w-100"
                                    status={createLoader}
                                    onPress={_handleConfirmCreate} />
                            </div>
                        </div>
                    }
                </div>
            </div>
        </div >
    )
});


ManageCompetitionModal.defaultProps = {
    closeModal: () => { }
}

export default ManageCompetitionModal;